<template>
  <div class="optimization-report-header d-flex justify-content-between">
    <div class="optimization-report-title">
      <div class="d-flex mb-2">
        <div class="d-flex">
          <h1>
            <router-link :to="assignmentPage">
              <a class="back-to position-absolute"><mdicon name="ChevronLeft" /></a>
            </router-link>
          </h1>
          <span class="ml-4 report-title"> Optimization report </span>
        </div>
        <InfoAlert v-show="!!alertData.class" class="ml-3" :class="alertData.class">
          {{ alertData.text }}
        </InfoAlert>
      </div>
      <p class="ml-4 sub-title">
        {{ subtitle }}
      </p>
    </div>

    <ul class="optimization-report-option mb-0">
      <li>
        <b-button class="file-btn" :disabled="!sourceFile" :href="sourceFile" :download="true">
          <Tooltip :text="tooltipText">
            <mdicon name="Download" />
          </Tooltip>
        </b-button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import type { Assignment } from '@/types/Models/Assignment'
import { defineComponent, computed, type ComputedRef } from 'vue'
import {
  COMPLETE,
  LABELS_CREATING,
  LABELS_RELEASED,
  REPORT_ACCEPTED,
  REPORT_DENIED,
  REPORT_RELEASED,
} from '@/const/AssignmentStatuses'
import { useAccountStore } from '@/store/account'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { CUSTOMER_ASSIGNMENTS_DETAILS, STAFF_ASSIGNMENTS_DETAILS } from '@/types/router'
import Tooltip from '@/views/Components/Elements/Tooltip.vue'
import InfoAlert from '@/views/Components/PageParts/InfoAlert.vue'

export default defineComponent({
  name: 'ReportHeader',
  components: {
    Tooltip,
    InfoAlert,
  },
  setup() {
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const accountStore = useAccountStore()

    const assignment: ComputedRef<Assignment> = computed(() => assignmentSummaryStore.assignment as Assignment)
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)

    const assignmentPage: ComputedRef<RouterLink> = computed(() => ({
      name: isStaff.value ? STAFF_ASSIGNMENTS_DETAILS : CUSTOMER_ASSIGNMENTS_DETAILS,
      params: { id: assignment.value.reference },
    }))

    const sourceFile: ComputedRef<string> = computed(() => assignment.value?.optimisationReportUrl || '')
    const tooltipText: ComputedRef<string> = computed(() => (sourceFile.value ? 'Download Report' : ''))

    const alertData: ComputedRef<{ class: string; text: string }> = computed(() => {
      const { status } = assignment.value
      switch (status) {
        case REPORT_DENIED:
          return {
            class: 'danger',
            text: 'Report denied',
          }
        case REPORT_ACCEPTED:
        case LABELS_CREATING:
        case LABELS_RELEASED:
        case COMPLETE:
          return {
            class: 'success',
            text: 'Report accepted',
          }
        case REPORT_RELEASED:
          return {
            class: 'secondary',
            text: 'Report sent',
          }
        default:
          return {
            class: '',
            text: '',
          }
      }
    })

    const subtitle: ComputedRef<string> = computed(() => {
      return isStaff.value
        ? 'You can send this report to the client for approval or cancel to make changes to the assignment and create a new report.'
        : 'You can come back to the report later, it isn’t necessary to make the decision right now 🙂'
    })

    return {
      assignment,
      isStaff,
      assignmentPage,
      sourceFile,
      tooltipText,
      alertData,
      subtitle,
    }
  },
})
</script>
